import { Field, getIn, useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";
import type { FC } from "react";
import { useAppSelector } from "@app/hooks";
import { selectIsFetchingData } from "@app/features/builder/builderSlice";
import { FEModules } from "@utils/moduleList";
import FormRow from "@components/input/formRow/FormRow";
import Skeleton from "react-loading-skeleton";
import TranslatableText from "@components/translations/TranslatableText";
import type { InputGroup, TranslationKey } from "@typedefs/modulesInterfaces";
import { FieldType } from "@typedefs/modulesInterfaces";
import type { FinalForm } from "../SetupAssistantFinish";
import { LucAdditionalInputs } from "./LucAdditionalInputs";

interface SpecialCheckboxesInputGroup extends InputGroup {
	type: FieldType.CHECKBOXES;
	options: { name: TranslationKey; id: string | number }[]
}

export const SpecialCheckBoxesInputGroup: FC<SpecialCheckboxesInputGroup> = ({
	inputName,
	index,
	comment,
	help,
	options,
	label,
	disabled,
}) => {
	const [name, setName] = useState<string>("");
	const isFetchingData: boolean = useAppSelector(selectIsFetchingData);
	const { errors, touched, values } = useFormikContext<FinalForm>();
	const listIncludesLuc = useMemo(
		() => values.modulesList.includes(`${FEModules.LandUseChange}`),
		[values.modulesList],
	);

	useEffect(() => {
		setName(
			typeof inputName === "function" ? inputName(index ?? 0) : inputName,
		);

		return () => {
			setName("");
		};
	}, [inputName, index]);

	return (
		<FormRow
			style={{ height: "fit-content" }}
			rowComment={comment}
			rowHelp={help}
			error={
				getIn(errors, name) && getIn(touched, name)
					? getIn(errors, name)
					: undefined
			}
		>
			<div
				role="group"
				aria-labelledby={label}
				className="d-flex f-column w-100"
			>
				{isFetchingData ? (
					<Skeleton height="100%" count={3} />
				) : (
					options.map((option) => (
						<div className="d-flex f-column" key={option.id}>
							<label className="clickable d-flex align-items-center pb-1 w-fit">
								<Field
									type="checkbox"
									name={name}
									id={name}
									value={option.id}
									disabled={disabled}
								/>
								<div className="d-flex align-items-center">
									<TranslatableText className="ps-1 fs-12 ff-medium" translationKey={option.name} />
									<span>{listIncludesLuc ? "*" : ""}</span>
								</div>
							</label>
							{option.id === `${FEModules.LandUseChange}` && listIncludesLuc ? (
								<LucAdditionalInputs />
							) : null}
						</div>
					))
				)}
			</div>
		</FormRow>
	);
}; 